import {Tag} from '@wandb/weave/components/Tag';
import {Tooltip} from '@wandb/weave/components/Tooltip';
import React, {memo} from 'react';
import {useRouteMatch} from 'react-router-dom';

import {LARGE_PROJECT_RUN_VISIBILITY_NOTE} from '../../aria';
import {
  WBAgentRunCountListedContextSlice,
  WBAgentRunListPreviewContextSlice,
  WBAgentRunVisibilityContextSlice,
} from '../../aria/externalBindings/WBAgentContextSlices';
import {GROUP_PATHS} from '../../routes/workspace/groupWorkspacePaths';
import {SWEEP_PATHS} from '../../routes/workspace/sweepWorkspacePaths';
import {useSelectedCounts} from '../../state/graphql/selectedCountQuery';
import type * as CustomRunNamesTypes from '../../state/views/customRunNames/types';
import type * as TempSelectionsTypes from '../../state/views/tempSelections/types';
import {activeFilterCountFromStored} from '../../util/filterV2';
import {Skeleton} from '../Skeleton';
import {
  DEFAULT_GROUPING_AGGREGATION_LIMIT,
  useGroupingAggregationLimit,
} from './hooks/useGroupingAggregationLimit';
import {useLeanGroupedRunsQueryRamp} from './hooks/useLeanGroupedRunsQuery';
import {
  type RunsCountResult,
  useIsLargeProject,
  useIsProjectRunCountStale,
  useNumCrossProjectRunsCount,
  useProjectRunsCount,
  useSweepRunsCount,
} from './hooks/useRunSelectorCount';
import {getRunsCountLabel, isRunListNarrowed} from './runsCountTagUtils';
import {
  useCurrentRunSetRef,
  useRunSelectorProjectInfo,
  useRunSetFilters,
  useRunSetHasSearchQuery,
} from './RunSelectorRunSetContext';

function useListedCountQueryArgs(
  onlyShowSelected?: boolean,
  skipTotalRunsCount?: boolean
): {
  skip: boolean;
} {
  const filters = useRunSetFilters();
  const hasSearchQuery = useRunSetHasSearchQuery();
  const isListNarrowed = isRunListNarrowed({
    activeFilterCount: activeFilterCountFromStored(filters),
    hasSearchQuery,
    onlyShowSelected: Boolean(onlyShowSelected),
  });
  return {
    skip: Boolean(skipTotalRunsCount) || !isListNarrowed,
  };
}

type RunsCountTagViewProps = RunsCountTagProps & {
  totalRuns: RunsCountResult;
  // True when new runs have likely been logged since the count loaded on a large
  // project, so the count may undercount and we append "+". Sweeps pass false.
  isCountStale: boolean;
};
const RunsCountTagView = ({
  tempSelectionsRef,
  customRunNamesRef,
  onlyShowSelected,
  isGrouped,
  hideSelectedCount,
  totalRuns,
  isCountStale,
  useLeanGroupedRunsQuery,
}: RunsCountTagViewProps) => {
  const runSetRef = useCurrentRunSetRef();
  const crossProjectCount = useNumCrossProjectRunsCount();
  const isLargeProject = useIsLargeProject();

  const selectedCounts = useSelectedCounts(
    runSetRef,
    tempSelectionsRef,
    customRunNamesRef,
    {
      countAllListedRuns: true,
      onlyShowSelected,
      // Only poll the (uncached) filtered count on projects small enough that
      // the aggregation is cheap. On large projects it can take many seconds
      // per run, so we keep it delete/interaction-driven to avoid re-running it
      // every poll tick per viewer. See useIsLargeProject.
      pollOnTick: !isLargeProject,
    }
  );

  // The configured cap (possibly raised above the default via the
  // max_runs_counted_grouping_in_run_selector dynamic config) is only honored
  // server-side on the lean grouped-runs query path
  // (RunSelectorGroupConnectionResolver.effectiveMaxRuns). Every other grouped
  // path (table expanded, pinned columns, unsupported grouping) reverts to
  // the default cap, so the displayed limit must match.
  const configuredAggregationLimit = useGroupingAggregationLimit();
  const groupingAggregationLimit = useLeanGroupedRunsQuery
    ? configuredAggregationLimit
    : DEFAULT_GROUPING_AGGREGATION_LIMIT;
  // Only warn about the reverted limit when the entity is ramped into the
  // lean query (so a raised limit could apply) but this view fell off the
  // lean path (e.g. grouping by an unsupported field).
  const {entityName} = useRunSelectorProjectInfo();
  const isLeanGroupedRunsQueryRampEnabled =
    useLeanGroupedRunsQueryRamp(entityName);
  const hasRevertedToDefaultLimit =
    isLeanGroupedRunsQueryRampEnabled &&
    !useLeanGroupedRunsQuery &&
    configuredAggregationLimit > DEFAULT_GROUPING_AGGREGATION_LIMIT;

  // The agent's run-visibility signal is the eyeball-on run count. That is
  // selectedCount computed with the real visibility selection, so we query it
  // independently of the listed count, which clears the selection to count table
  // rows and would otherwise report every filtered run as visible.
  //
  // On large projects we skip this query entirely: computing the exact split
  // would run an uncached aggregation over the whole project, so we send a note
  // instead of a count (see WBAgentRunVisibilityContextSlice).
  const visibleCounts = useSelectedCounts(
    runSetRef,
    tempSelectionsRef,
    customRunNamesRef,
    {skip: isLargeProject}
  );

  const listedCount = selectedCounts.selectedCount;

  // num_runs_hidden = runs listed in the selector but hidden from the workspace
  // (listed − visible). Both counts come from useSelectedCounts, so pinned-run
  // and selection handling stay consistent. On large projects the visible count
  // isn't queried, so we send a note instead of a number.
  const numRunsHiddenValue = isLargeProject
    ? LARGE_PROJECT_RUN_VISIBILITY_NOTE
    : Math.max(0, listedCount - visibleCounts.selectedCount);

  const isTempSelectionActive =
    !hideSelectedCount && selectedCounts.tempSelectedCount > 0;
  const isAggregationLimited =
    Boolean(isGrouped) &&
    !isTempSelectionActive &&
    selectedCounts.selectedCount > groupingAggregationLimit;
  const showCountSkeleton =
    !isTempSelectionActive && !isAggregationLimited && totalRuns.loading;

  const countLabel = getRunsCountLabel({
    isTempSelectionActive,
    tempSelectedCount: selectedCounts.tempSelectedCount,
    selectedCount: selectedCounts.selectedCount,
    listedCount,
    totalRunsCount: totalRuns.count + crossProjectCount,
    hasTotalRunsCountError: totalRuns.error != null,
    totalRunsCountSkipped: totalRuns.skipped,
    countCap: isAggregationLimited ? groupingAggregationLimit : undefined,
    // Large project with new runs logged since load (same case as the header's
    // stale-count info icon): the count may be low, so append "+". The signal is
    // already gated to large projects by useIsProjectRunCountStale.
    isCountStale,
  });

  const revertedLimitWarning = hasRevertedToDefaultLimit
    ? ` Your higher grouped-run limit (${configuredAggregationLimit.toLocaleString()}) only applies to optimized grouping views, so this view has reverted to the default ${DEFAULT_GROUPING_AGGREGATION_LIMIT.toLocaleString()}.`
    : '';
  const aggregationLimitTooltip = isAggregationLimited
    ? `The run selector shows only the most recent ${groupingAggregationLimit.toLocaleString()} runs and the groups affiliated with those runs. ${(
        selectedCounts.selectedCount - groupingAggregationLimit
      ).toLocaleString()} of the runs matching your filters aren't included in these group totals.${revertedLimitWarning}`
    : undefined;

  return (
    <>
      <WBAgentRunCountListedContextSlice count={listedCount} />
      <WBAgentRunListPreviewContextSlice
        runSetRef={runSetRef}
        customRunNamesRef={customRunNamesRef}
      />
      <WBAgentRunVisibilityContextSlice
        visibleCount={isLargeProject ? undefined : visibleCounts.selectedCount}
        hiddenCount={numRunsHiddenValue}
        listOnlyVisibleRuns={Boolean(onlyShowSelected)}
      />
      <span data-test="selected-count" className="normal-case">
        {showCountSkeleton ? (
          <Skeleton className="rounded-sm opacity-60" height={22} width={80} />
        ) : isAggregationLimited ? (
          // TODO: switch to Counter once it supports icon/color/tooltip
          <Tooltip
            content={aggregationLimitTooltip}
            contentClassName="max-w-xs"
            trigger={
              <Tag
                color="gold"
                showIcon
                iconName="warning"
                label={countLabel}
              />
            }
          />
        ) : (
          <Tag color="moon" label={countLabel} />
        )}
      </span>
    </>
  );
};

const ProjectRunsCountTag = ({
  skipTotalRunsCount,
  ...props
}: RunsCountTagProps & {skipTotalRunsCount?: boolean}) => {
  const {skip} = useListedCountQueryArgs(
    props.onlyShowSelected,
    skipTotalRunsCount
  );
  const totalRuns = useProjectRunsCount({skip});
  // Read the new-runs signal independently: `totalRuns` is skipped when the list
  // isn't narrowed, so its own `isCountStale` would always be false here.
  const isCountStale = useIsProjectRunCountStale();
  return (
    <RunsCountTagView
      {...props}
      totalRuns={totalRuns}
      isCountStale={isCountStale}
    />
  );
};

interface RunsCountTagProps {
  tempSelectionsRef: TempSelectionsTypes.Ref;
  customRunNamesRef?: CustomRunNamesTypes.Ref;
  onlyShowSelected?: boolean;
  isGrouped?: boolean;
  hideSelectedCount?: boolean;
  useLeanGroupedRunsQuery?: boolean;
}

const SweepRunsCountTag = (props: RunsCountTagProps) => {
  const {skip} = useListedCountQueryArgs(props.onlyShowSelected);
  const totalRuns = useSweepRunsCount({skip});
  return (
    <RunsCountTagView
      {...props}
      totalRuns={totalRuns}
      // Sweeps don't get the "+": lastActive is a project-wide signal and can't
      // tell whether new runs landed in this specific sweep.
      isCountStale={false}
    />
  );
};

const RunsCountTagComp = (props: RunsCountTagProps) => {
  const isSweepPage = useRouteMatch(SWEEP_PATHS) != null;
  const isGroupPage = useRouteMatch(GROUP_PATHS) != null;
  return isSweepPage ? (
    <SweepRunsCountTag {...props} />
  ) : (
    <ProjectRunsCountTag {...props} skipTotalRunsCount={isGroupPage} />
  );
};

export const RunsCountTag = memo(RunsCountTagComp);
